import type {
  IChatListItem,
  IPaginatedChatListResponse,
} from '@/apiClient/types/chat'
import { queryClient } from '@/lib/queryClient'

/**
 * Product cap on the sidebar's "Pinned" section. The API's own ceiling is far higher
 * (100 ids per `PATCH /chats/pins`), so this one is enforced here: it keeps the section
 * short enough to stay scannable and lets the UI say "you can pin up to N" instead of
 * spending a request to be refused.
 */
export const MAX_PINNED_CHATS = 10

/** Shown when the user tries to pin past {@link MAX_PINNED_CHATS}. */
export const PINNED_CHATS_LIMIT_MESSAGE = `You can pin up to ${MAX_PINNED_CHATS} chats. Unpin one to pin another.`

/**
 * How long a drag-reorder waits before it is sent. A reorder replaces the whole list, so
 * every intermediate order in a burst of drags would be written only to be overwritten —
 * long enough to swallow the quick successive drops of tidying the section, short enough
 * that the write still feels committed by the time attention moves on. Pinning and
 * unpinning bypass this and send at once (see the chat store's `setChatPinned`).
 */
export const PINNED_CHATS_COMMIT_DEBOUNCE_MS = 800

/** Prefix every chat list query shares (`list()` keys as `['chats', 'list', filters]`). */
const CHAT_LIST_QUERY_KEY = ['chats', 'list'] as const

const byUpdatedAtDesc = (a: IChatListItem, b: IChatListItem) =>
  b.updated_at - a.updated_at

/**
 * Move the pinned/unpinned split of every cached chat list onto `orderedChats`, so the
 * sidebar shows the new "Pinned" section the moment `PATCH /chats/pins` succeeds rather
 * than when its refetch lands.
 *
 * Mirrors what the API does on the next GET: `pinned` becomes exactly the given order,
 * those rows leave `items`, and rows that lost their pin rejoin `items` in date order —
 * which is where the list below the section would have shown them all along.
 *
 * Only page-1 payloads are touched: later pages carry unpinned rows and an empty
 * `pinned`, so rewriting them would invent a section the server never sent.
 */
export function applyPinnedChatsToCaches(orderedChats: IChatListItem[]) {
  const pinnedIds = new Set(orderedChats.map((chat) => chat.id))

  queryClient.setQueriesData<IPaginatedChatListResponse>(
    { queryKey: CHAT_LIST_QUERY_KEY },
    (old) => {
      if (!old || old.page !== 1) return old

      const cachedPinned = old.pinned ?? []
      // Prefer the cached row over the caller's: a rename or a retention bump that
      // landed after the caller read the list would otherwise be rolled back here.
      const knownById = new Map(
        [...cachedPinned, ...old.items].map((chat) => [chat.id, chat]),
      )
      const pinned = orderedChats.map((chat) => knownById.get(chat.id) ?? chat)

      const unpinnedRows = cachedPinned.filter(
        (chat) => !pinnedIds.has(chat.id),
      )
      const keptItems = old.items.filter((chat) => !pinnedIds.has(chat.id))
      const items = unpinnedRows.length
        ? [...keptItems, ...unpinnedRows].sort(byUpdatedAtDesc)
        : keptItems

      return {
        ...old,
        pinned,
        items,
        // `total` counts unpinned chats only, so it moves by whatever `items` gained
        // or lost — the pinned rows themselves never counted towards it.
        total: Math.max(0, old.total + items.length - old.items.length),
      }
    },
  )
}

/**
 * Drop a deleted chat from the cached "Pinned" sections. The API unpins it as part of
 * the delete, but the list refetch that follows would leave the row on screen as a dead
 * entry until it lands.
 */
export function removeChatFromPinnedCache(chatId: string) {
  queryClient.setQueriesData<IPaginatedChatListResponse>(
    { queryKey: CHAT_LIST_QUERY_KEY },
    (old) => {
      if (!old?.pinned?.length) return old
      const pinned = old.pinned.filter((chat) => chat.id !== chatId)
      return pinned.length === old.pinned.length ? old : { ...old, pinned }
    },
  )
}
